'use client'

import { ReactNode, useState } from 'react'

interface ConfirmOption {
    message: string
    value: number
    className?: string
}

interface ConfirmDialogProps {
    isOpen: boolean
    title: string
    description?: string
    options?: ConfirmOption[] | null
    onClose: () => void
    onConfirm: (values?: number[]) => void
}

export default function ConfirmDialog({
                                          isOpen,
                                          title,
                                          description,
                                          options,
                                          onClose,
                                          onConfirm,
                                      }: ConfirmDialogProps) {
    const [selectedValues, setSelectedValues] = useState<number[]>([])

    if (!isOpen) return null

    const handleCheckboxChange = (value: number) => {
        setSelectedValues(prev =>
            prev.includes(value)
                ? prev.filter(v => v !== value)
                : [...prev, value]
        )
    }

    const handleConfirm = () => {
        if (options && selectedValues.length === 0) return
        onConfirm(options ? selectedValues : undefined)
        onClose()
    }

    return (
        <div className="fixed inset-0 flex items-center justify-center z-[100] bg-black/50 backdrop-blur-sm transition-opacity duration-300">
            <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md border border-gray-100 transform transition-all duration-300 scale-95 hover:scale-100">
                <div className="flex items-start">

                    <div className=" w-full">
                        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
                        {description && <p className="mt-2 text-md text-gray-500">{description}</p>}

                        {options && (
                            <div className="mt-4 space-y-3">
                                {options.map((option) => (
                                    <label
                                        key={option.value}
                                        className={`flex items-start p-3 rounded-lg border transition-colors duration-200 
                                            ${selectedValues.includes(option.value)
                                            ? 'border-blue-200 bg-blue-50'
                                            : 'border-gray-200 hover:bg-gray-50'}`}
                                    >
                                        <div className="flex items-center h-5">
                                            <input
                                                type="checkbox"
                                                checked={selectedValues.includes(option.value)}
                                                onChange={() => handleCheckboxChange(option.value)}
                                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="ml-3 text-md">
                                            <span className="font-medium text-gray-700">{option.message}</span>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                    <button
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                        onClick={onClose}
                    >
                        {options ? 'Không thực hiện' : 'Hủy'}
                    </button>
                    <button
                        className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 ${
                            options
                                ? selectedValues.length > 0
                                    ? 'bg-blue-600 hover:bg-blue-700'
                                    : 'bg-gray-400 cursor-not-allowed'
                                : 'bg-red-600 hover:bg-red-700'
                        }`}
                        onClick={handleConfirm}
                        disabled={options ? selectedValues.length === 0 : false}
                    >
                        {options ? 'Xác nhận thực hiện' : 'Xác nhận'}
                    </button>
                </div>
            </div>
        </div>
    )
}